/**
 * Gift card delivery options
 * ----------------------------------------------------------------------------
 * Drives the two modes rendered by snippets/gift-card-recipient-form.liquid:
 * send the card to yourself with a message, or send it to someone else.
 *
 * The markup ships with both checkboxes and every field DISABLED. That is the
 * no-JavaScript fallback: a disabled control is not submitted, so without this
 * script the buyer sees plain optional fields and the hidden "if_present"
 * control tells Shopify to send the card only if an address was typed.
 *
 * This script's job:
 *   - enable the checkboxes so the buyer can opt in explicitly
 *   - keep the two modes mutually exclusive; ticking one unticks the other
 *   - enable only the active mode's fields, so an untouched form adds no line
 *     item properties at all and the cart line stays clean
 *   - require an address once a mode is chosen (Shopify rejects a message
 *     without one)
 *   - count down the 200 characters Shopify allows in each message
 *   - keep the two-column product layout tall enough for the expanded fields
 *
 * The modes are checkboxes rather than radios on purpose: radios cannot be
 * cleared, and "neither" is a real, common choice here -- it is the default,
 * where the card simply goes to the checkout address.
 *
 * No framework, no custom elements, to match the rest of this theme. Binds per
 * form, so more than one gift card form on a page still works.
 */
(function () {
  'use strict';

  var MAX_MESSAGE_CHARS = 200;

  function initForm(root) {
    var checkboxes = root.querySelectorAll('[data-recipient-checkbox]');
    var control = root.querySelector('[data-recipient-control]');

    // Bail quietly rather than throwing: this snippet only renders on gift card
    // products, and other scripts on the page should not be taken down with it.
    if (!checkboxes.length || !control) {
      return;
    }

    // Pair each checkbox with the field group it owns, so the rest of the code
    // never has to re-query or care how many modes there are.
    var modes = [];

    for (var i = 0; i < checkboxes.length; i++) {
      var box = checkboxes[i];
      var name = box.getAttribute('data-recipient-mode');
      var fields = root.querySelector('[data-recipient-fields="' + name + '"]');

      if (fields) {
        modes.push({
          checkbox: box,
          fields: fields,
          inputs: fields.querySelectorAll('[data-recipient-field]'),
          email: fields.querySelector('[data-recipient-email]'),
          message: fields.querySelector('[data-recipient-message]'),
          messageValue: fields.querySelector('[data-recipient-message-value]'),
          from: fields.querySelector('[data-recipient-from]'),
          counter: fields.querySelector('[data-recipient-counter]')
        });
      }
    }

    if (!modes.length) {
      return;
    }

    // The checkboxes are the buyer's explicit opt-in once scripting is
    // available, so the "send it if an address happens to be present" fallback
    // steps aside.
    for (var j = 0; j < modes.length; j++) {
      modes[j].checkbox.disabled = false;
    }
    control.disabled = true;

    function sync(changed) {
      // Mutually exclusive: choosing one mode clears the other. Done before
      // anything is enabled so the losing mode's fields are never left live.
      if (changed && changed.checked) {
        for (var m = 0; m < modes.length; m++) {
          if (modes[m].checkbox !== changed) {
            modes[m].checkbox.checked = false;
          }
        }
      }

      for (var k = 0; k < modes.length; k++) {
        var mode = modes[k];
        var on = mode.checkbox.checked;

        mode.fields.hidden = !on;

        for (var n = 0; n < mode.inputs.length; n++) {
          mode.inputs[n].disabled = !on;
        }

        // An address is mandatory once a mode is chosen, because Shopify
        // rejects a message without one ("Email can't be blank"). Set here
        // rather than in the markup so the no-JavaScript path, where the fields
        // are simply optional, is not turned into a form nobody can submit.
        if (mode.email) {
          if (on) {
            mode.email.setAttribute('required', 'required');
          } else {
            mode.email.removeAttribute('required');
          }
        }
      }

      // Focus the first field of the mode just opened, so keyboard users are
      // not left hunting for fields that appeared elsewhere on the page. Only
      // on a real interaction, never on the initial pass.
      if (changed && changed.checked && document.activeElement === changed) {
        var opened = null;

        for (var p = 0; p < modes.length; p++) {
          if (modes[p].checkbox === changed) {
            opened = modes[p];
          }
        }

        if (opened && opened.inputs.length) {
          opened.inputs[0].focus();
        }
      }

      fitColumn();
    }

    // Keep the two-column product layout tall enough for the expanded fields.
    //
    // On lap-and-up the theme lays the product page out as one column of blocks
    // with the info column pulled out and pinned beside it:
    //
    //   .product-block-list__item--info { position: absolute; height: 100%; }
    //
    // so the right column's height is whatever the LEFT column happens to be.
    // That is fine until the right column grows taller, which is exactly what
    // opening these fields does on a product with 40-odd amount buttons: the
    // card runs past the bottom of .product-block-list and the next section
    // ("You may also like") paints straight over the fields.
    //
    // The column is absolutely positioned, so it cannot push its parent and no
    // amount of min-height on the column itself helps. The parent has to be
    // told how tall to be, and only the browser knows that, so it is measured.
    //
    // Deliberately scoped to this script rather than fixed in the stylesheet:
    // the rule above is used by every product page, and gift cards are the only
    // place currently able to overflow it.
    var infoColumn = root.closest ? root.closest('.product-block-list__item--info') : null;
    var blockList = infoColumn ? infoColumn.closest('.product-block-list') : null;

    function fitColumn() {
      if (!infoColumn || !blockList) {
        return;
      }

      // Below the breakpoint the column is static and stacks normally, so there
      // is nothing to correct and a leftover min-height would only add a gap.
      blockList.style.minHeight = '';

      if (window.getComputedStyle(infoColumn).position !== 'absolute') {
        return;
      }

      // scrollHeight is the content height even though the element itself is
      // clamped to height:100%, which is what makes the overflow measurable.
      var needed = infoColumn.scrollHeight;

      if (needed > blockList.offsetHeight) {
        blockList.style.minHeight = needed + 'px';
      }
    }

    var resizeTimer = null;

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fitColumn, 150);
    });

    // What gets signed on to the end of the message. Empty when there is no
    // From field, or nothing typed in it -- so the "to me" mode, which has no
    // From, composes to exactly what the buyer typed.
    function signature(mode) {
      if (!mode.from) {
        return '';
      }

      var name = mode.from.value.trim();

      return name ? '\n- ' + name : '';
    }

    // Keep the hidden properties[Message] equal to message + signature, and keep
    // the visible box from being able to overflow the 200 characters Shopify
    // allows for the two of them combined.
    function composeMessage(mode) {
      if (!mode.message || !mode.messageValue) {
        return;
      }

      var sig = signature(mode);
      var room = MAX_MESSAGE_CHARS - sig.length;

      mode.message.setAttribute('maxlength', room > 0 ? room : 0);

      // Lengthening the name can leave text already typed over the new limit;
      // maxlength does not retroactively trim, so trim here.
      if (mode.message.value.length > room) {
        mode.message.value = mode.message.value.substring(0, room > 0 ? room : 0);
      }

      mode.messageValue.value = mode.message.value + sig;
    }

    function updateCounter(mode) {
      if (!mode.message || !mode.counter) {
        return;
      }

      // Counts the composed length, so the number reflects what Shopify will
      // actually measure rather than only what is visible in the box.
      var used = mode.message.value.length + signature(mode).length;
      var left = MAX_MESSAGE_CHARS - used;
      var template = mode.counter.getAttribute('data-template');

      if (!template) {
        // Keep the server-rendered wording; only the number changes. Stored on
        // first use so the translated string is never rebuilt in JavaScript.
        template = mode.counter.textContent.trim();
        mode.counter.setAttribute('data-template', template);
      }

      mode.counter.textContent = template.replace(/\d+/, String(left));
    }

    for (var q = 0; q < modes.length; q++) {
      (function (mode) {
        mode.checkbox.addEventListener('change', function () {
          sync(mode.checkbox);
        });

        if (mode.message) {
          mode.message.addEventListener('input', function () {
            composeMessage(mode);
            updateCounter(mode);
          });
        }

        if (mode.from) {
          mode.from.addEventListener('input', function () {
            composeMessage(mode);
            updateCounter(mode);
          });
        }
      })(modes[q]);
    }

    // Validate before the theme's add-to-cart handler runs.
    //
    // Two reasons this cannot be left to the browser or the server:
    //
    // - The theme adds to cart over ajax from a click handler that calls
    //   preventDefault(), so the form is never natively submitted and the
    //   browser's own required/type=email checks never fire.
    // - Shopify does validate server-side and returns 422, but the theme prints
    //   the response's `description` field, which for a bad address is the
    //   literal string "{ email: [ 'is invalid' ] }". Correct, and useless to a
    //   shopper. Catching it here means they get the normal browser message
    //   against the offending field instead.
    //
    // Capture phase on the form: the theme's listener is delegated from an
    // ancestor in the bubble phase, so capturing here runs first and
    // stopPropagation keeps it from firing at all.
    var form = root.closest('form');

    if (form) {
      form.addEventListener(
        'click',
        function (event) {
          var button = event.target.closest
            ? event.target.closest('[data-action="add-to-cart"], .product-form__add-button')
            : null;

          if (!button) {
            return;
          }

          for (var r = 0; r < modes.length; r++) {
            var inputs = modes[r].inputs;

            for (var s = 0; s < inputs.length; s++) {
              if (!inputs[s].disabled && !inputs[s].checkValidity()) {
                event.preventDefault();
                event.stopPropagation();
                inputs[s].reportValidity();
                return;
              }
            }
          }
        },
        true
      );
    }

    sync(null);

    for (var t = 0; t < modes.length; t++) {
      composeMessage(modes[t]);
      updateCounter(modes[t]);
    }
  }

  function init() {
    var forms = document.querySelectorAll('[data-recipient-form]');

    for (var i = 0; i < forms.length; i++) {
      initForm(forms[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
